<app-page pageTitle="{{ title | translate }}">
    <!-- Tools Section with Dropdowns -->
    @if (showDepartmentStatistics) {
        <ng-container tools>
            <div class="flex items-center gap-3">
                <ng-content select="[tools]"></ng-content>
                <!-- Year Dropdown -->
                <div class="flex flex-col gap-1">
                    <app-year-dropdown
                        [yearLoader]="yearLoader"
                        (yearChange)="onYearChange($event)"
                    ></app-year-dropdown>
                </div>
                <!-- Evaluation Type Dropdown -->
                <ng-select
                    [items]="evaluationTypes || []"
                    [ngModel]="selectedEvaluationType"
                    [clearable]="false"
                    [searchable]="false"
                    (ngModelChange)="onEvaluationTypeChange($event)"
                    bindLabel="name"
                    placeholder="{{
                        'translate_select_evaluation_type' | translate
                    }}"
                    class="min-w-[200px]"
                ></ng-select>
                <!-- Measurement Cycle Toggle Buttons -->
                <div class="flex items-center gap-1">
                    @for (cycle of measurementCycles; track cycle) {
                        <button
                            (click)="onMeasurementCycleChange(cycle.id)"
                            class="rounded-lg px-3 py-1.5 text-xs font-medium transition-colors"
                            [class.bg-primary-50]="
                                selectedMeasurementCycle === cycle.id
                            "
                            [class.text-primary-700]="
                                selectedMeasurementCycle === cycle.id
                            "
                            [class.border]="
                                selectedMeasurementCycle !== cycle.id
                            "
                            [class.border-slate-200]="
                                selectedMeasurementCycle !== cycle.id
                            "
                            [class.text-slate-600]="
                                selectedMeasurementCycle !== cycle.id
                            "
                            [class.hover:bg-slate-50]="
                                selectedMeasurementCycle !== cycle.id
                            "
                        >
                            {{ cycle.name }}
                        </button>
                    }
                </div>
            </div>
        </ng-container>
    }

    <div content class="flex flex-col gap-6">
        <!-- Existing evaluation statistics (shown when not in department mode) -->
        @if (!showDepartmentStatistics) {
            <app-evaluation-statistics
                #statistics
                (evaluationChange)="evaluationChange.emit($event); reload()"
                type="{{ type }}"
            ></app-evaluation-statistics>
        }

        <!-- Department Statistics Section -->
        @if (showDepartmentStatistics) {
            <div>
                <!-- Department Statistics Card -->
                @if (selectedEvaluationType && selectedMeasurementCycle) {
                    <div
                        class="overflow-hidden rounded-xl border border-slate-200 bg-white"
                    >
                        <!-- Card Header with Accent Line -->
                        <div
                            class="relative flex items-center justify-between border-b border-slate-100 px-5 py-4"
                        >
                            <div
                                class="absolute bottom-0 top-0 w-1 bg-primary-500 ltr:left-0 rtl:right-0"
                            ></div>
                            <div
                                class="flex items-center gap-3 ltr:pl-3 rtl:pr-3"
                            >
                                <h3 class="font-semibold text-slate-800">
                                    {{
                                        'translate_departments_statistics'
                                            | translate
                                    }}
                                </h3>
                                <!-- Current Parent Department Badge -->
                                @if (currentParentDepartment) {
                                    <span
                                        class="inline-flex items-center rounded-full bg-primary-100 px-2.5 py-1 text-xs font-medium text-primary-700"
                                    >
                                        {{ currentParentDepartment.name }}
                                    </span>
                                }
                            </div>
                            <!-- Back Navigation Button -->
                            @if (currentParentDepartment) {
                                <button
                                    (click)="goToPreviousDepartmentStatistics()"
                                    class="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
                                >
                                    <i
                                        class="fa-light fa-arrow-right ltr:fa-arrow-left text-xs"
                                    ></i>
                                    {{ 'translate_back' | translate }}
                                </button>
                            }
                        </div>
                        <!-- Card Content -->
                        <div class="p-5">
                            <!-- Loading State -->
                            @if (isLoadingStatistics) {
                                <div
                                    class="flex min-h-[200px] items-center justify-center"
                                >
                                    <app-loading-ring
                                        [spinnerBorderWidth]="4"
                                        [spinnerDim]="80"
                                    ></app-loading-ring>
                                </div>
                            }
                            <!-- Department Cards Grid -->
                            @if (
                                !isLoadingStatistics &&
                                departmentStatistics?.length
                            ) {
                                <div
                                    [appCardStagger]="
                                        departmentStatistics.length
                                    "
                                    class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
                                >
                                    @for (
                                        item of departmentStatistics;
                                        track item
                                    ) {
                                        <ng-container
                                            [ngTemplateOutlet]="
                                                departmentTemplateRef
                                            "
                                            [ngTemplateOutletContext]="{ item }"
                                        ></ng-container>
                                    }
                                </div>
                            }
                            <!-- No Data State -->
                            @if (
                                !isLoadingStatistics &&
                                !departmentStatistics?.length
                            ) {
                                <div
                                    class="flex min-h-[200px] flex-col items-center justify-center p-8 text-center"
                                >
                                    <div
                                        class="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100"
                                    >
                                        <i
                                            class="fa-light fa-building text-xl text-slate-400"
                                        ></i>
                                    </div>
                                    <p class="font-medium text-slate-700">
                                        {{
                                            'translate_no_data_available'
                                                | translate
                                        }}
                                    </p>
                                </div>
                            }
                        </div>
                    </div>
                }
            </div>
        }

        <!-- Existing content for evaluations list -->
        @if (hasListContent && !showDepartmentStatistics) {
            <app-content
                contentTitle="{{ 'translate_evaluations' | translate }}"
            >
                <ng-container content>
                    <ng-content select="[list]"></ng-content>
                </ng-container>
            </app-content>
        }
    </div>
</app-page>

<!-- Department Template -->
<ng-template #departmentTemplateRef let-item="item">
    <a
        animate.enter="flipping-card-enter"
        [routerLink]="['', 'kpi-evaluation']"
        [queryParams]="{
            filter:
                {
                    data: {
                        departmentIds: [item.department.id],
                        cycles: selectedMeasurementCycle
                            ? [selectedMeasurementCycle]
                            : [],
                        year: currentYear,
                    },
                } | json,
        }"
        class="group block rounded-xl border border-slate-200 bg-white p-5 no-underline shadow-sm transition-all hover:border-slate-300 hover:no-underline hover:shadow-md"
    >
        <!-- Department Name -->
        <h3
            class="mb-4 overflow-hidden text-ellipsis whitespace-nowrap font-semibold text-slate-800"
            [title]="item.department.name"
        >
            {{ item.department.name }}
        </h3>

        <!-- Stats Row -->
        <div class="mb-4 flex items-end justify-between">
            <!-- Average Score (Primary Metric) -->
            <div>
                <p class="text-sm text-slate-500">
                    {{ 'translate_average_score' | translate }}
                </p>
                <p class="text-2xl font-bold text-slate-800">
                    {{
                        item.averageScore !== null
                            ? (item.averageScore * 100 | round: 0) + '%'
                            : 'N/A'
                    }}
                </p>
            </div>
            <!-- Total Evaluations -->
            <div class="text-end">
                <p class="text-sm text-slate-500">
                    {{ 'translate_total_evaluations' | translate }}
                </p>
                <p class="text-lg font-semibold text-slate-600">
                    {{ item.totalEvaluation }}
                </p>
            </div>
        </div>

        <!-- Progress Bar -->
        <div class="mb-4">
            <div class="h-2 overflow-hidden rounded-full bg-slate-100">
                <div
                    class="h-full rounded-full transition-all"
                    [class.bg-emerald-500]="
                        item.averageScore !== null && item.averageScore >= 0.8
                    "
                    [class.bg-amber-500]="
                        item.averageScore !== null &&
                        item.averageScore >= 0.6 &&
                        item.averageScore < 0.8
                    "
                    [class.bg-rose-500]="
                        item.averageScore !== null && item.averageScore < 0.6
                    "
                    [class.bg-slate-300]="item.averageScore === null"
                    [style.width.%]="
                        item.averageScore !== null ? item.averageScore * 100 : 0
                    "
                ></div>
            </div>
        </div>

        <!-- Navigation to Child Departments -->
        @if (item.childrenCount > 0) {
            <div
                class="flex items-center justify-between border-t border-slate-100 pt-4"
            >
                <span class="text-sm text-slate-500">
                    {{ item.childrenCount }}
                    {{ 'translate_sub_departments' | translate }}
                </span>
                <button
                    type="button"
                    (click)="
                        goToNextDepartmentStatistics(item.department);
                        $event.preventDefault();
                        $event.stopPropagation()
                    "
                    class="inline-flex items-center gap-1 text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
                >
                    {{ 'translate_view_details' | translate }}
                    <i
                        class="fa-light fa-arrow-left ltr:fa-arrow-right text-xs"
                    ></i>
                </button>
            </div>
        }
    </a>
</ng-template>

<ng-template #controlsTemplate let-item="item">
    <div class="flex flex-row items-center gap-2">
        <!-- Show evaluations -->
        @if (item.evaluateActionAbility.canRead) {
            <button
                (click)="onClickEvaluate(item)"
                class="btn btn-sm btn-white whitespace-nowrap"
            >
                {{ 'translate_evaluations_list' | translate }}
            </button>
        }
    </div>
</ng-template>
